import { useQuery, useMutation, gql } from '@apollo/client';
import { useState } from 'react';
import { Table, Dropdown, Label, Segment, Header, Input, Button, Checkbox } from 'semantic-ui-react';

/**
 * Who can read, post to, or manage one stream.
 *
 * The list comes from the server already filtered to people who can actually sign in — a grant
 * to somebody with no credentials is a row that looks like access and can never be exercised.
 *
 * Access that comes from a **role** is shown but not editable. A ROLE grant is rewritten on every
 * expansion, so changing it here would be undone at the next one and would look like it had
 * worked. Changing what a role grants is the roles page's job.
 */

const GET_PEOPLE = gql`
  query StreamPeople($streamId: Int!, $search: String) {
    streamPeople(streamId: $streamId, search: $search) {
      membershipId userId name userName level sourceType isEditable
    }
  }
`;

const SET_ACCESS = gql`
  mutation SetStreamAccess($streamId: Int!, $membershipId: Int!, $level: String!) {
    setStreamAccess(streamId: $streamId, membershipId: $membershipId, level: $level)
  }
`;

/**
 * 6.9 — which supporting organisations read this stream.
 *
 * Owner side only, deliberately: a supporter organisation has no concept of a subscription, no
 * list and no self-unsubscribe. It sees a feed. The organisation that owns the stream decides
 * who reads it.
 */
const GET_SUBSCRIBERS = gql`
  query StreamSubscribers($streamId: Int!) {
    streamSubscribers(streamId: $streamId) {
      organisationId name subscribed isEditable
    }
  }
`;

const SET_SUBSCRIPTION = gql`
  mutation SetStreamSubscription($streamId: Int!, $subscriberOrganisationId: Int!, $subscribed: Boolean!) {
    setStreamSubscription(streamId: $streamId, subscriberOrganisationId: $subscriberOrganisationId, subscribed: $subscribed)
  }
`;

const LEVELS = [
  { key: 'NONE', text: 'No access', value: 'NONE' },
  { key: 'VIEW', text: 'Can read', value: 'VIEW' },
  { key: 'CONTRIBUTE', text: 'Can post', value: 'CONTRIBUTE' },
  { key: 'MANAGE', text: 'Can manage', value: 'MANAGE' },
];

interface Props {
  streamId: number;
  streamName: string;
  onError: (message: string | null) => void;
}

export const StreamAccess = ({ streamId, streamName, onError }: Props) => {
  // Two queries because they answer two questions: the list is "who can see this", the search is
  // "who do I mean". Mixing them is what made the list 1,863 rows of mostly nobody.
  const { data, loading, refetch } = useQuery(GET_PEOPLE, {
    variables: { streamId },
    fetchPolicy: 'network-only',
  });

  const [search, setSearch] = useState('');
  const { data: found } = useQuery(GET_PEOPLE, {
    variables: { streamId, search },
    skip: search.trim().length < 2,
    fetchPolicy: 'network-only',
  });

  const [setAccess] = useMutation(SET_ACCESS);
  const { data: subs, refetch: refetchSubs } = useQuery(GET_SUBSCRIBERS, {
    variables: { streamId },
    fetchPolicy: 'network-only',
  });
  const [setSubscription] = useMutation(SET_SUBSCRIPTION);

  if (loading) {
    return <Segment secondary><p>Loading…</p></Segment>;
  }

  const withAccess = data?.streamPeople ?? [];
  const candidates = search.trim().length < 2 ? [] : (found?.streamPeople ?? []);

  const grant = async (membershipId: number, level: string) => {
    onError(null);
    try {
      await setAccess({ variables: { streamId, membershipId, level } });
      setSearch('');
      await refetch();
    } catch (e: any) {
      onError(e?.message ?? 'Could not change that.');
    }
  };

  return (
    <Segment secondary>
      <Header as="h5">Who can see “{streamName}”</Header>
      <p style={{ color: '#767676', fontSize: '0.9em' }}>
        {withAccess.length === 0
          ? 'Nobody yet, apart from anyone whose role covers every stream.'
          : `${withAccess.length} ${withAccess.length === 1 ? 'person has' : 'people have'} access.`}
      </p>

      <Table compact size="small">
        <Table.Body>
          {withAccess.map((p: any) => (
            <Table.Row key={p.membershipId}>
              <Table.Cell>{p.name ?? p.userName}</Table.Cell>
              <Table.Cell collapsing>
                {p.isEditable
                  ? (
                    <Dropdown
                      selection
                      compact
                      value={p.level ?? 'NONE'}
                      options={LEVELS}
                      onChange={(_, d) => grant(p.membershipId, d.value as string)}
                    />
                  )
                  : (
                    <Label size="tiny">
                      {p.level?.toLowerCase()} — from a role
                    </Label>
                  )}
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>

      {(subs?.streamSubscribers ?? []).length > 0 && (
        <>
          <Header as="h5" style={{ marginTop: '1.2em' }}>Organisations that support us</Header>
          <p style={{ color: '#767676', fontSize: '0.9em' }}>
            Ticking one lets everybody in that organisation read this stream, including people
            who join later.
          </p>
          <Table compact size="small">
            <Table.Body>
              {(subs?.streamSubscribers ?? []).map((o: any) => (
                <Table.Row key={o.organisationId}>
                  <Table.Cell>{o.name}</Table.Cell>
                  <Table.Cell collapsing>
                    {o.isEditable
                      ? (
                        <Checkbox
                          toggle
                          checked={o.subscribed}
                          onChange={async (_, d) => {
                            onError(null);
                            try {
                              await setSubscription({
                                variables: {
                                  streamId,
                                  subscriberOrganisationId: o.organisationId,
                                  subscribed: d.checked === true,
                                },
                              });
                              await refetchSubs();
                            } catch (e: any) {
                              onError(e?.message ?? 'Could not change that.');
                            }
                          }}
                        />
                      )
                      : <Label size="tiny">reads every stream</Label>}
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </>
      )}

      <Header as="h5" style={{ marginTop: '1.2em' }}>Give somebody access</Header>
      <Input
        icon="search"
        placeholder="Search by name"
        value={search}
        style={{ width: '100%', maxWidth: '24em' }}
        onChange={(e) => setSearch(e.target.value)}
      />

      {search.trim().length >= 2 && (
        candidates.length === 0
          ? <p style={{ color: '#767676', marginTop: '0.6em' }}>Nobody found who does not already have access.</p>
          : (
            <Table compact size="small" style={{ marginTop: '0.6em' }}>
              <Table.Body>
                {candidates.map((p: any) => (
                  <Table.Row key={p.membershipId}>
                    <Table.Cell>{p.name ?? p.userName}</Table.Cell>
                    <Table.Cell collapsing textAlign="right">
                      {/* Granting is a deliberate act with a level attached, so the choice is
                          made here rather than adding somebody at a default and editing after. */}
                      <Button size="tiny" onClick={() => grant(p.membershipId, 'VIEW')}>Can read</Button>
                      <Button size="tiny" onClick={() => grant(p.membershipId, 'CONTRIBUTE')}>Can post</Button>
                      <Button size="tiny" onClick={() => grant(p.membershipId, 'MANAGE')}>Can manage</Button>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          )
      )}
    </Segment>
  );
};
